import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardActionArea,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import { useGate } from '../../hooks/useGate';
import { GATE_ACTIONS } from '../../constants/gateConfig';
import UsageAlertBanner from '../gate/UsageAlertBanner';
import GridOnIcon from '@mui/icons-material/GridOn';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import RoofingIcon from '@mui/icons-material/Roofing';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import VerticalShadesIcon from '@mui/icons-material/VerticalShades';
import CalculateIcon from '@mui/icons-material/Calculate';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TerrainIcon from '@mui/icons-material/Terrain';
import ExploreIcon from '@mui/icons-material/Explore';
import FoundationIcon from '@mui/icons-material/Foundation';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import StraightenIcon from '@mui/icons-material/Straighten';
import {
  CATEGORIES,
  MATERIALS,
  UNIT_OPTIONS,
  calculateWithRange,
  formatNumber
} from '../../utils/materialEstimatorData';

// SVG Illustration Components for Brick Bonds
const StretcherBondSVG = () => (
  <svg viewBox="0 0 200 100" style={{ width: '100%', maxWidth: 200, height: 'auto' }}>
    {/* Row 1 */}
    <rect x="5" y="5" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="68" y="5" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="131" y="5" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 2 - offset */}
    <rect x="35" y="28" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="98" y="28" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="161" y="28" width="30" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 3 */}
    <rect x="5" y="51" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="68" y="51" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="131" y="51" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 4 - offset */}
    <rect x="35" y="74" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="98" y="74" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="161" y="74" width="30" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Mortar lines */}
    <line x1="0" y1="27" x2="200" y2="27" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
    <line x1="0" y1="50" x2="200" y2="50" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
    <line x1="0" y1="73" x2="200" y2="73" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
  </svg>
);

const EnglishBondSVG = () => (
  <svg viewBox="0 0 200 100" style={{ width: '100%', maxWidth: 200, height: 'auto' }}>
    {/* Row 1 - Stretcher course */}
    <rect x="5" y="5" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="68" y="5" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="131" y="5" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 2 - Header course (bricks showing end face) */}
    <rect x="5" y="28" width="28" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="36" y="28" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="67" y="28" width="28" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="98" y="28" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="129" y="28" width="28" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="160" y="28" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 3 - Stretcher course */}
    <rect x="35" y="51" width="60" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="98" y="51" width="60" height="20" fill="#D2691E" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="161" y="51" width="30" height="20" fill="#CD853F" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Row 4 - Header course */}
    <rect x="20" y="74" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="51" y="74" width="28" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="82" y="74" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="113" y="74" width="28" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="144" y="74" width="28" height="20" fill="#DAA520" stroke="#8B4513" strokeWidth="1" rx="1"/>
    <rect x="175" y="74" width="20" height="20" fill="#B8860B" stroke="#8B4513" strokeWidth="1" rx="1"/>
    {/* Mortar lines */}
    <line x1="0" y1="27" x2="200" y2="27" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
    <line x1="0" y1="50" x2="200" y2="50" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
    <line x1="0" y1="73" x2="200" y2="73" stroke="#A0A0A0" strokeWidth="2" strokeDasharray="2,2"/>
  </svg>
);

const MaterialEstimator = ({ language = 'en', initialCategory = null }) => {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [selectedUnits, setSelectedUnits] = useState({
    length: 'ft',
    area: 'sqft',
    thickness: 'inch'
  });
  const [result, setResult] = useState(null);

  const { checkAccess, GateModalComponent } = useGate();

  // Handle initialCategory prop changes from landing page navigation
  useEffect(() => {
    if (initialCategory && initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory);
      setSelectedMaterial(null);
      setInputValues({});
      setResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory]);

  const content = {
    en: {
      title: 'Material Estimator',
      selectCategory: 'Select Category',
      selectMaterial: 'Select Material',
      calculate: 'Calculate',
      reset: 'New Calculation',
      result: 'You Need',
      min: 'Min',
      max: 'Max',
      alsoNeeded: 'Also Required',
      note: 'Note',
      unitSettings: 'Unit Settings',
      lengthUnit: 'Length Unit',
      areaUnit: 'Area Unit',
      thicknessUnit: 'Thickness Unit',
    },
    my: {
      title: 'Penganggar Bahan',
      selectCategory: 'Pilih Kategori',
      selectMaterial: 'Pilih Bahan',
      calculate: 'Kira',
      reset: 'Kira Semula',
      result: 'Anda Perlukan',
      min: 'Min',
      max: 'Maks',
      alsoNeeded: 'Juga Diperlukan',
      note: 'Nota',
      unitSettings: 'Tetapan Unit',
      lengthUnit: 'Unit Panjang',
      areaUnit: 'Unit Luas',
      thicknessUnit: 'Unit Ketebalan',
    }
  };

  const t = content[language] || content.en;

  const categoryIcons = {
    walls: <GridOnIcon />,
    floors: <ViewModuleIcon />,
    roofing: <RoofingIcon />,
    concrete: <ViewInArIcon />,
    ceiling: <VerticalShadesIcon />,
    earthwork: <TerrainIcon />,
    surveying: <ExploreIcon />,
    foundation: <FoundationIcon />,
    plastering: <FormatPaintIcon />,
  };

  const availableMaterials = useMemo(() => {
    if (!selectedCategory) return [];
    return Object.values(MATERIALS[selectedCategory] || {});
  }, [selectedCategory]);

  const currentMaterial = useMemo(() => {
    if (!selectedCategory || !selectedMaterial) return null;
    return MATERIALS[selectedCategory]?.[selectedMaterial];
  }, [selectedCategory, selectedMaterial]);

  const isFormValid = useMemo(() => {
    if (!currentMaterial) return false;
    return currentMaterial.inputs.every(input => {
      const value = inputValues[input.id];
      if (input.default !== undefined && !value) return true;
      return value && parseFloat(value) > 0;
    });
  }, [currentMaterial, inputValues]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedMaterial(null);
    setInputValues({});
    setResult(null);
  };

  const handleMaterialSelect = (materialId) => {
    setSelectedMaterial(materialId);
    const material = MATERIALS[selectedCategory]?.[materialId];
    if (material) {
      const defaults = {};
      material.inputs.forEach(input => {
        if (input.default !== undefined) {
          defaults[input.id] = input.default;
        }
      });
      setInputValues(defaults);
      // Reset units to default when changing material
      setSelectedUnits({
        length: 'ft',
        area: 'sqft',
        thickness: 'inch'
      });
    }
    setResult(null);
  };

  const handleInputChange = (inputId, value) => {
    setInputValues(prev => ({ ...prev, [inputId]: value }));
    setResult(null);
  };

  const handleUnitChange = (unitType, value) => {
    setSelectedUnits(prev => ({ ...prev, [unitType]: value }));
    setResult(null);
  };

  const handleCalculate = () => {
    if (!currentMaterial || !isFormValid) return;
    if (!checkAccess(GATE_ACTIONS.MATERIAL_ESTIMATE)) return;

    const parsedInputs = {};
    currentMaterial.inputs.forEach(input => {
      const value = inputValues[input.id];
      parsedInputs[input.id] = value ? parseFloat(value) : input.default || 0;
    });

    const calculation = currentMaterial.calculate(parsedInputs, selectedUnits);

    const resultWithRanges = {
      primary: {
        ...calculation.primary,
        ...calculateWithRange(calculation.primary.value)
      },
      secondary: calculation.secondary.map(item => ({
        ...item,
        ...calculateWithRange(item.value)
      })),
      assumptions: currentMaterial.assumptions
    };

    setResult(resultWithRanges);
  };

  // Get the required unit types for current material
  const getRequiredUnitTypes = () => {
    if (!currentMaterial) return [];
    const unitTypes = new Set();
    currentMaterial.inputs.forEach(input => {
      if (input.unitType) {
        unitTypes.add(input.unitType);
      }
    });
    return Array.from(unitTypes);
  };

  const handleReset = () => {
    setSelectedCategory(null);
    setSelectedMaterial(null);
    setInputValues({});
    setSelectedUnits({
      length: 'ft',
      area: 'sqft',
      thickness: 'inch'
    });
    setResult(null);
  };

  const handleBack = () => {
    if (result) {
      setResult(null);
    } else if (selectedMaterial) {
      setSelectedMaterial(null);
      setInputValues({});
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
  };

  // Category Selection
  const renderCategorySelection = () => (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {t.selectCategory}
      </Typography>
      <Grid container spacing={1.5}>
        {Object.values(CATEGORIES).map((category) => (
          <Grid item xs={4} key={category.id}>
            <Card
              variant="outlined"
              sx={{
                textAlign: 'center',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
            >
              <CardActionArea onClick={() => handleCategorySelect(category.id)} sx={{ p: 2 }}>
                <Box sx={{ color: 'primary.main', mb: 0.5 }}>
                  {categoryIcons[category.id]}
                </Box>
                <Typography variant="caption" fontWeight={500} sx={{ lineHeight: 1.2, display: 'block' }}>
                  {language === 'my' ? category.labelMy : category.label}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Get small preview SVG for material
  const getMaterialPreviewSVG = (materialId) => {
    // --- Walls ---
    if (materialId === 'bricks' || materialId === 'bricksByArea') {
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            <rect x="2" y="2" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="34" y="2" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="66" y="2" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="17" y="14" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="49" y="14" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="2" y="26" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="34" y="26" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="66" y="26" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="17" y="38" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="49" y="38" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
          </svg>
        </Box>
      );
    }

    if (materialId === 'bricksFullWall') {
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            <rect x="2" y="2" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="34" y="2" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="66" y="2" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="2" y="14" width="14" height="10" fill="#B8860B" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="18" y="14" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="34" y="14" width="14" height="10" fill="#B8860B" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="50" y="14" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="66" y="14" width="14" height="10" fill="#B8860B" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="82" y="14" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="17" y="26" width="30" height="10" fill="#CD853F" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="49" y="26" width="30" height="10" fill="#D2691E" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="10" y="38" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="26" y="38" width="14" height="10" fill="#B8860B" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="42" y="38" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="58" y="38" width="14" height="10" fill="#B8860B" stroke="#8B4513" strokeWidth="0.5"/>
            <rect x="74" y="38" width="14" height="10" fill="#DAA520" stroke="#8B4513" strokeWidth="0.5"/>
          </svg>
        </Box>
      );
    }

    // --- Foundation ---
    if (materialId === 'stripFooting') {
      // Cross-section: ground line, trench, concrete strip footing with rebar dots
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            {/* Ground */}
            <rect x="0" y="0" width="100" height="18" fill="#8B7355" opacity="0.3"/>
            <line x1="0" y1="18" x2="100" y2="18" stroke="#6B4226" strokeWidth="1"/>
            {/* Trench walls */}
            <line x1="20" y1="18" x2="20" y2="48" stroke="#6B4226" strokeWidth="0.5" strokeDasharray="2,1"/>
            <line x1="80" y1="18" x2="80" y2="48" stroke="#6B4226" strokeWidth="0.5" strokeDasharray="2,1"/>
            {/* Concrete footing */}
            <rect x="15" y="30" width="70" height="18" fill="#A0A0A0" stroke="#707070" strokeWidth="0.8" rx="1"/>
            {/* Rebar dots */}
            <circle cx="28" cy="42" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            <circle cx="50" cy="42" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            <circle cx="72" cy="42" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            <circle cx="28" cy="35" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            <circle cx="50" cy="35" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            <circle cx="72" cy="35" r="2" fill="#444" stroke="#333" strokeWidth="0.5"/>
            {/* Column stub */}
            <rect x="40" y="10" width="20" height="20" fill="#B0B0B0" stroke="#707070" strokeWidth="0.8"/>
          </svg>
        </Box>
      );
    }

    if (materialId === 'padFooting') {
      // Top-down view: square pad footing with column in center
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            {/* Ground */}
            <rect x="0" y="0" width="100" height="50" fill="#8B7355" opacity="0.15"/>
            {/* Pad footing (square) */}
            <rect x="10" y="5" width="80" height="40" fill="#A0A0A0" stroke="#707070" strokeWidth="1" rx="1"/>
            {/* Diagonal hatch lines for concrete */}
            <line x1="10" y1="15" x2="20" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="10" y1="25" x2="30" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="10" y1="35" x2="40" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="10" y1="45" x2="50" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="20" y1="45" x2="60" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="30" y1="45" x2="70" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="40" y1="45" x2="80" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="50" y1="45" x2="90" y2="5" stroke="#888" strokeWidth="0.3"/>
            <line x1="60" y1="45" x2="90" y2="15" stroke="#888" strokeWidth="0.3"/>
            <line x1="70" y1="45" x2="90" y2="25" stroke="#888" strokeWidth="0.3"/>
            <line x1="80" y1="45" x2="90" y2="35" stroke="#888" strokeWidth="0.3"/>
            {/* Column (center square) */}
            <rect x="35" y="15" width="30" height="20" fill="#B0B0B0" stroke="#555" strokeWidth="1"/>
            {/* Rebar dots in column */}
            <circle cx="40" cy="20" r="1.5" fill="#333"/>
            <circle cx="60" cy="20" r="1.5" fill="#333"/>
            <circle cx="40" cy="30" r="1.5" fill="#333"/>
            <circle cx="60" cy="30" r="1.5" fill="#333"/>
          </svg>
        </Box>
      );
    }

    if (materialId === 'retainingWall') {
      // Cross-section: L-shaped retaining wall with soil behind
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            {/* Soil behind wall */}
            <polygon points="55,2 98,2 98,42 55,42" fill="#8B7355" opacity="0.3"/>
            {/* Soil hatch */}
            <circle cx="65" cy="10" r="1" fill="#6B4226" opacity="0.4"/>
            <circle cx="80" cy="8" r="1" fill="#6B4226" opacity="0.4"/>
            <circle cx="72" cy="20" r="1" fill="#6B4226" opacity="0.4"/>
            <circle cx="88" cy="15" r="1" fill="#6B4226" opacity="0.4"/>
            <circle cx="75" cy="32" r="1" fill="#6B4226" opacity="0.4"/>
            <circle cx="90" cy="28" r="1" fill="#6B4226" opacity="0.4"/>
            {/* Vertical wall stem */}
            <rect x="42" y="2" width="13" height="40" fill="#A0A0A0" stroke="#707070" strokeWidth="0.8"/>
            {/* Horizontal base slab */}
            <rect x="10" y="38" width="80" height="10" fill="#A0A0A0" stroke="#707070" strokeWidth="0.8"/>
            {/* Rebar in wall */}
            <line x1="48" y1="4" x2="48" y2="38" stroke="#444" strokeWidth="1.2"/>
            {/* Rebar in base */}
            <line x1="14" y1="43" x2="86" y2="43" stroke="#444" strokeWidth="1.2"/>
            {/* Ground line on front */}
            <line x1="2" y1="38" x2="42" y2="38" stroke="#6B4226" strokeWidth="0.8"/>
          </svg>
        </Box>
      );
    }

    if (materialId === 'soling') {
      // Cross-section: layer of stones/gravel as sub-base
      return (
        <Box sx={{ width: 60, height: 30, mb: 0.5 }}>
          <svg viewBox="0 0 100 50" style={{ width: '100%', height: '100%' }}>
            {/* Ground/soil below */}
            <rect x="0" y="35" width="100" height="15" fill="#8B7355" opacity="0.3"/>
            {/* Soling layer - irregular stones */}
            <polygon points="5,20 15,18 20,22 18,28 8,28" fill="#9E9E9E" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="20,19 32,17 35,22 30,28 22,27" fill="#BDBDBD" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="34,18 45,16 48,21 44,28 36,27" fill="#A0A0A0" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="47,19 58,17 62,22 58,28 49,27" fill="#B0B0B0" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="61,18 72,16 76,21 72,28 63,27" fill="#9E9E9E" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="75,19 85,17 90,22 86,28 77,27" fill="#BDBDBD" stroke="#707070" strokeWidth="0.5"/>
            <polygon points="88,20 96,18 98,23 95,28 89,27" fill="#A0A0A0" stroke="#707070" strokeWidth="0.5"/>
            {/* Concrete/sand layer on top */}
            <rect x="0" y="10" width="100" height="8" fill="#C0C0C0" stroke="#999" strokeWidth="0.5" opacity="0.6"/>
            {/* Ground line */}
            <line x1="0" y1="10" x2="100" y2="10" stroke="#6B4226" strokeWidth="0.8"/>
            {/* Sand fill between stones */}
            <rect x="0" y="28" width="100" height="7" fill="#D2B48C" opacity="0.4"/>
          </svg>
        </Box>
      );
    }

    return null;
  };

  // Material Selection
  const renderMaterialSelection = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Chip
          size="small"
          icon={categoryIcons[selectedCategory]}
          label={language === 'my' ? CATEGORIES[selectedCategory]?.labelMy : CATEGORIES[selectedCategory]?.label}
          color="primary"
          variant="outlined"
        />
      </Box>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
        {t.selectMaterial}
      </Typography>
      <Grid container spacing={1}>
        {availableMaterials.map((material) => (
          <Grid item xs={6} key={material.id}>
            <Card
              variant="outlined"
              sx={{
                '&:hover': { borderColor: 'primary.main' },
              }}
            >
              <CardActionArea onClick={() => handleMaterialSelect(material.id)} sx={{ p: 1.5, textAlign: 'center' }}>
                {getMaterialPreviewSVG(material.id)}
                <Typography variant="body2" fontWeight={500}>
                  {language === 'my' ? material.labelMy : material.label}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Unit Selector Component
  const renderUnitSelector = () => {
    const requiredUnitTypes = getRequiredUnitTypes();
    if (requiredUnitTypes.length === 0 || !currentMaterial?.unitSelectable) return null;

    return (
      <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <StraightenIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            {t.unitSettings}
          </Typography>
        </Box>
        <Grid container spacing={1.5}>
          {requiredUnitTypes.includes('length') && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.lengthUnit}</InputLabel>
                <Select
                  value={selectedUnits.length}
                  label={t.lengthUnit}
                  onChange={(e) => handleUnitChange('length', e.target.value)}
                >
                  {UNIT_OPTIONS.length.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {language === 'my' ? option.labelMy : option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {requiredUnitTypes.includes('area') && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.areaUnit}</InputLabel>
                <Select
                  value={selectedUnits.area}
                  label={t.areaUnit}
                  onChange={(e) => handleUnitChange('area', e.target.value)}
                >
                  {UNIT_OPTIONS.area.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {language === 'my' ? option.labelMy : option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          {requiredUnitTypes.includes('thickness') && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t.thicknessUnit}</InputLabel>
                <Select
                  value={selectedUnits.thickness}
                  label={t.thicknessUnit}
                  onChange={(e) => handleUnitChange('thickness', e.target.value)}
                >
                  {UNIT_OPTIONS.thickness.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {language === 'my' ? option.labelMy : option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  // Bond type illustration content
  const bondContent = {
    en: {
      stretcherTitle: 'Stretcher Bond (4.5" Wall)',
      stretcherDesc: 'Half-brick wall with bricks laid lengthwise. Uses ~5 bricks per sqft.',
      englishTitle: 'English Bond (9" Wall)',
      englishDesc: 'Full-brick wall alternating stretcher and header courses. Uses ~10 bricks per sqft.',
    },
    my: {
      stretcherTitle: 'Ikatan Stretcher (Dinding 4.5")',
      stretcherDesc: 'Dinding separuh bata dengan bata diletakkan memanjang. Guna ~5 bata per kaki persegi.',
      englishTitle: 'Ikatan Inggeris (Dinding 9")',
      englishDesc: 'Dinding bata penuh berselang seli antara stretcher dan header. Guna ~10 bata per kaki persegi.',
    }
  };

  const bc = bondContent[language] || bondContent.en;

  // Render brick bond illustration
  const renderBondIllustration = () => {
    if (!selectedMaterial || !selectedCategory === 'walls') return null;

    // Only show for brick-related materials
    if (selectedMaterial === 'bricks' || selectedMaterial === 'bricksByArea') {
      return (
        <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
          <StretcherBondSVG />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
            {bc.stretcherTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {bc.stretcherDesc}
          </Typography>
        </Card>
      );
    }

    if (selectedMaterial === 'bricksFullWall') {
      return (
        <Card variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover', textAlign: 'center' }}>
          <EnglishBondSVG />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
            {bc.englishTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {bc.englishDesc}
          </Typography>
        </Card>
      );
    }

    return null;
  };

  // Input Form
  const renderInputForm = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          {language === 'my' ? currentMaterial?.labelMy : currentMaterial?.label}
        </Typography>
      </Box>

      {/* Bond Type Illustration */}
      {renderBondIllustration()}

      {/* Unit Selector */}
      {renderUnitSelector()}

      {/* Input Fields */}
      <Card variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {currentMaterial?.inputs.map((input) => (
            <Grid item xs={12} key={input.id}>
              <TextField
                fullWidth
                size="small"
                type="number"
                inputMode="decimal"
                label={language === 'my' ? input.labelMy : input.label}
                value={inputValues[input.id] || ''}
                onChange={(e) => handleInputChange(input.id, e.target.value)}
                placeholder={input.default?.toString() || '0'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {input.unitType
                        ? (UNIT_OPTIONS[input.unitType]?.find(u => u.id === selectedUnits[input.unitType])?.id || input.unit)
                        : (language === 'my' ? input.unitMy : input.unit)
                      }
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Card>

      {/* Info Alert */}
      {currentMaterial?.assumptions && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="caption">
            {language === 'my' ? currentMaterial.assumptions.my : currentMaterial.assumptions.en}
          </Typography>
        </Alert>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleCalculate}
        disabled={!isFormValid}
        startIcon={<CalculateIcon />}
        size="large"
      >
        {t.calculate}
      </Button>
    </Box>
  );

  // Results
  const renderResults = () => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          {t.result}
        </Typography>
      </Box>

      {/* Primary Result */}
      <Card sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {language === 'my' ? result.primary.labelMy : result.primary.label}
          </Typography>
          <Typography variant="h3" fontWeight={700}>
            {formatNumber(result.primary.avg)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {language === 'my' ? result.primary.unitMy : result.primary.unit}
          </Typography>
          {result.primary.extra && (
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5, fontWeight: 500 }}>
              = {language === 'my' ? result.primary.extraMy : result.primary.extra}
            </Typography>
          )}
          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.2)' }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">{t.min}: {formatNumber(result.primary.min)}</Typography>
            <Typography variant="caption">{t.max}: {formatNumber(result.primary.max)}</Typography>
          </Box>
        </Box>
      </Card>

      {/* Secondary Results */}
      {result.secondary.length > 0 && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t.alsoNeeded}
            </Typography>
          </Box>
          <Divider />
          {result.secondary.map((item, index) => (
            <Box key={index}>
              <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {language === 'my' ? item.labelMy : item.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(item.min)} - {formatNumber(item.max)} {language === 'my' ? item.unitMy : item.unit}
                  </Typography>
                  {item.extra && (
                    <Typography variant="caption" display="block" color="primary.main" sx={{ mt: 0.5 }}>
                      = {language === 'my' ? item.extraMy : item.extra}
                    </Typography>
                  )}
                </Box>
                <Typography variant="h6" fontWeight={700} color="primary">
                  {formatNumber(item.avg)}
                </Typography>
              </Box>
              {index < result.secondary.length - 1 && <Divider />}
            </Box>
          ))}
        </Card>
      )}

      {/* Note */}
      <Card variant="outlined" sx={{ bgcolor: 'info.50', borderColor: 'info.200', mb: 2 }}>
        <Box sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>{t.note}:</strong> {language === 'my' ? result.assumptions.my : result.assumptions.en}
          </Typography>
        </Box>
      </Card>

      <Button
        fullWidth
        variant="outlined"
        onClick={handleReset}
        startIcon={<RefreshIcon />}
      >
        {t.reset}
      </Button>
    </Box>
  );

  const renderContent = () => {
    if (result) return renderResults();
    if (selectedMaterial) return renderInputForm();
    if (selectedCategory) return renderMaterialSelection();
    return renderCategorySelection();
  };

  return (
    <Box sx={{ p: 2, maxWidth: 480, mx: 'auto', pb: 10 }}>
      {/* Header */}
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
        {t.title}
      </Typography>

      {/* Usage Alert */}
      <UsageAlertBanner actionCode={GATE_ACTIONS.MATERIAL_ESTIMATE} language={language} />

      {/* Content */}
      {renderContent()}

      {/* Gate Modal */}
      <GateModalComponent />
    </Box>
  );
};

export default MaterialEstimator;
